import { useState, useEffect, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Department = { id: number; code: string; name: string; is_active: boolean }
type PersonLite = { id: number; full_name: string }
type PipelineLite = {
  id: number; name: string; is_default: boolean; is_active: boolean; stage_count: number
}

type Job = {
  id: number
  code: string
  title: string
  status: 'DRAFT' | 'APPROVED' | 'OPEN' | 'PAUSED' | 'CLOSED' | 'CANCELLED'
  employment_type: 'FULL_TIME' | 'PART_TIME' | 'CONTRACTOR' | 'INTERN'
  location_type: 'ONSITE' | 'REMOTE' | 'HYBRID'
  location_country: string
  location_city: string
  department: number | null
  department_name: string | null
  hiring_manager: number | null
  hiring_manager_name: string | null
  recruiter: number | null
  recruiter_name: string | null
  openings: number
  filled_count: number
  remaining_openings: number
  application_count: number
  posted_at: string | null
  created_at: string
}

type CreateInput = {
  title: string
  department: number | null
  hiring_manager: number | null
  recruiter: number | null
  pipeline: number | null
  employment_type: Job['employment_type']
  location_type: Job['location_type']
  openings: number
}

const STATUS_COLOR: Record<Job['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  APPROVED: 'bg-blue-100 text-blue-700',
  OPEN: 'bg-green-100 text-green-800',
  PAUSED: 'bg-amber-100 text-amber-700',
  CLOSED: 'bg-slate-100 text-slate-700',
  CANCELLED: 'bg-red-100 text-red-700',
}

const STATUS_LABEL: Record<Job['status'], string> = {
  DRAFT: 'Draft', APPROVED: 'Approved', OPEN: 'Open',
  PAUSED: 'Paused', CLOSED: 'Closed', CANCELLED: 'Cancelled',
}

const EMPLOYMENT_LABEL: Record<Job['employment_type'], string> = {
  FULL_TIME: 'Full-time', PART_TIME: 'Part-time',
  CONTRACTOR: 'Contractor', INTERN: 'Intern',
}

const LOCATION_LABEL: Record<Job['location_type'], string> = {
  ONSITE: 'On-site', REMOTE: 'Remote', HYBRID: 'Hybrid',
}

const STATUS_FILTERS: ('ALL' | 'OPEN' | 'PAUSED' | 'CLOSED' | 'DRAFT')[] =
  ['ALL', 'OPEN', 'PAUSED', 'CLOSED', 'DRAFT']

export default function ATSJobs() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const jobsQ = useQuery({
    queryKey: ['ats', 'jobs'],
    queryFn: async () => (await api.get<Job[]>('/ats/jobs/')).data,
  })
  const departmentsQ = useQuery({
    queryKey: ['hr', 'departments'],
    queryFn: async () => (await api.get<Department[]>('/hr/departments/')).data,
  })
  const personsQ = useQuery({
    queryKey: ['hr', 'persons'],
    queryFn: async () => (await api.get<PersonLite[]>('/hr/persons/')).data,
  })
  const pipelinesQ = useQuery({
    queryKey: ['ats', 'pipelines'],
    queryFn: async () => (await api.get<PipelineLite[]>('/ats/pipelines/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())

  // When pipelines load (or the drawer opens), preselect the org default
  // so the picker starts on the right value.
  useEffect(() => {
    if (!creating) return
    if (draft.pipeline) return
    const def = (pipelinesQ.data || []).find((p) => p.is_default && p.is_active)
    if (def) setDraft((d) => ({ ...d, pipeline: def.id }))
  }, [creating, pipelinesQ.data, draft.pipeline])
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_FILTERS)[number]>('OPEN')

  function openCreate() {
    setDraft(emptyDraft())
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<Job>('/ats/jobs/', {
      ...draft,
      // Backend auto-fills pipeline from the org default when omitted —
      // we send it explicitly so the recruiter's pick is honoured.
      status: 'DRAFT',
      description: '',
      requirements: '',
      responsibilities: '',
      location_country: '',
      location_city: '',
      salary_currency_code: 'EUR',
      source_channels: [],
      notes: '',
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['ats', 'jobs'] })
      toast.success('Job created')
      setCreating(false)
      navigate(`/admin/ats/jobs/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/ats/jobs/${id}/`),
    onSuccess: () => {
      toast.success('Job deleted')
      qc.invalidateQueries({ queryKey: ['ats', 'jobs'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const jobs = jobsQ.data || []
  const departments = (departmentsQ.data || []).filter((d) => d.is_active)
  const persons = personsQ.data || []
  const pipelines = (pipelinesQ.data || []).filter((p) => p.is_active)
  const filtered = statusFilter === 'ALL' ? jobs : jobs.filter((j) => j.status === statusFilter)

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Jobs</h2>
          <p className="text-sm text-muted-foreground">
            {jobs.length} total · {jobs.filter((j) => j.status === 'OPEN').length} open ·
            {' '}{jobs.reduce((sum, j) => sum + j.application_count, 0)} applications
          </p>
        </div>
        <Button onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> New job</Button>
      </div>

      <div className="mb-4 flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Filter:</span>
        {STATUS_FILTERS.map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 rounded ${statusFilter === s ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}>
            {s === 'ALL' ? 'All' : STATUS_LABEL[s as Job['status']] ?? s}
          </button>
        ))}
      </div>

      {jobsQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          {jobs.length === 0
            ? 'No jobs yet. Create one to start tracking candidates.'
            : 'No jobs match this filter.'}
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Code</Th>
                <Th>Title</Th>
                <Th>Department</Th>
                <Th>Hiring manager</Th>
                <Th>Type</Th>
                <Th>Location</Th>
                <Th className="text-right">Openings</Th>
                <Th className="text-right">Apps</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((j) => (
                <tr
                  key={j.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/ats/jobs/${j.id}`)}
                >
                  <Td className="font-mono text-xs">{j.code}</Td>
                  <Td>
                    <div className="font-medium text-foreground">{j.title}</div>
                  </Td>
                  <Td className="text-sm">{j.department_name || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-sm">{j.hiring_manager_name || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-xs">{EMPLOYMENT_LABEL[j.employment_type]}</Td>
                  <Td className="text-xs">
                    {LOCATION_LABEL[j.location_type]}
                    {j.location_country && <span className="text-muted-foreground"> · {j.location_country}</span>}
                  </Td>
                  <Td className="text-right text-sm">
                    {j.filled_count}/{j.openings}
                  </Td>
                  <Td className="text-right text-sm">{j.application_count}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[j.status]}`}>
                      {STATUS_LABEL[j.status]}
                    </span>
                  </Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/ats/jobs/${j.id}`)}>
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => confirm(`job ${j.code} — ${j.title}`, () => deleteMut.mutate(j.id))}
                          className="text-destructive"
                        >
                          Delete permanently
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New job"
        description="Capture the essentials. Description, requirements, salary range, and other details are filled in on the job record page."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="job-title">Title *</FieldLabel>
            <Input id="job-title" required value={draft.title}
              onChange={(ev) => setDraft({ ...draft, title: ev.target.value })}
              placeholder="Senior Software Engineer" />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="job-dept">Department</FieldLabel>
              <SearchableSelect
                id="job-dept"
                value={draft.department ? String(draft.department) : NONE}
                onChange={(v) => setDraft({ ...draft, department: v === NONE ? null : Number(v) })}
                placeholder="— none —"
                options={[
                  { value: NONE, label: '— none —' },
                  ...departments.map((d) => ({ value: String(d.id), label: d.name, hint: d.code })),
                ]}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="job-type">Employment type</FieldLabel>
              <Select value={draft.employment_type}
                onValueChange={(v) => setDraft({ ...draft, employment_type: v as Job['employment_type'] })}>
                <SelectTrigger id="job-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(EMPLOYMENT_LABEL) as Job['employment_type'][]).map((k) => (
                    <SelectItem key={k} value={k}>{EMPLOYMENT_LABEL[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="job-hm">Hiring manager</FieldLabel>
              <SearchableSelect
                id="job-hm"
                value={draft.hiring_manager ? String(draft.hiring_manager) : NONE}
                onChange={(v) => setDraft({ ...draft, hiring_manager: v === NONE ? null : Number(v) })}
                placeholder="— none —"
                options={[
                  { value: NONE, label: '— none —' },
                  ...persons.map((p) => ({ value: String(p.id), label: p.full_name })),
                ]}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="job-rec">Recruiter</FieldLabel>
              <SearchableSelect
                id="job-rec"
                value={draft.recruiter ? String(draft.recruiter) : NONE}
                onChange={(v) => setDraft({ ...draft, recruiter: v === NONE ? null : Number(v) })}
                placeholder="— none —"
                options={[
                  { value: NONE, label: '— none —' },
                  ...persons.map((p) => ({ value: String(p.id), label: p.full_name })),
                ]}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="job-loc">Location type</FieldLabel>
              <Select value={draft.location_type}
                onValueChange={(v) => setDraft({ ...draft, location_type: v as Job['location_type'] })}>
                <SelectTrigger id="job-loc"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(LOCATION_LABEL) as Job['location_type'][]).map((k) => (
                    <SelectItem key={k} value={k}>{LOCATION_LABEL[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="job-openings">Openings</FieldLabel>
              <Input id="job-openings" type="number" min={1} value={draft.openings}
                onChange={(ev) => setDraft({ ...draft, openings: Math.max(1, Number(ev.target.value) || 1) })} />
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="job-pipeline">Hiring pipeline</FieldLabel>
            <SearchableSelect
              id="job-pipeline"
              value={draft.pipeline ? String(draft.pipeline) : NONE}
              onChange={(v) => setDraft({ ...draft, pipeline: v === NONE ? null : Number(v) })}
              placeholder={pipelines.length === 0 ? 'No pipelines configured' : '— pick a pipeline —'}
              options={[
                { value: NONE, label: '— use organisation default —' },
                ...pipelines.map((p) => ({
                  value: String(p.id),
                  label: p.name,
                  hint: `${p.stage_count} stage${p.stage_count === 1 ? '' : 's'}${p.is_default ? ' · default' : ''}`,
                })),
              ]}
            />
            {pipelines.length === 0 && (
              <p className="text-xs text-amber-700 mt-1">
                No pipelines exist yet — create one under Recruitment → Pipelines first.
              </p>
            )}
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return {
    title: '', department: null, hiring_manager: null, recruiter: null,
    pipeline: null,
    employment_type: 'FULL_TIME', location_type: 'ONSITE', openings: 1,
  }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
